(ns hai.mini-apps.add-pill
  "The ＋ every mini-app card header carries: a capsule naming what it adds —
   ＋ Note, ＋ Calendar — so a dashboard's cards read alike and each ＋ says what
   it will do before it is pressed. The shape of the Add buttons the notes and
   tasks pages wear, in the card headers.

   Outline and ink come from the card, since each wears its own tint: [[pill]]
   is shape only, and every host outlines it in the deeper sibling of its
   header colour (`--note-add-fill` and friends). [[pill]] is also the
   capsule on its own, for a card whose ＋ carries a menu beside it
   ([[hai.tasks.add-pill]])."
  (:require
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

;; The button fills the capsule rather than floating inside it, so its hover
;; wash reaches the host's outline — as the halves of the tasks pill do. It
;; stretches to the capsule's height instead of carrying one of its own, so a
;; host that makes the capsule taller for touch resizes the whole control.
(o/defstyled pill :div
  {:display "inline-flex" :align-items "center" :gap "2px"
   :height "34px"
   :padding "0"
   :box-sizing "border-box"
   :border-radius "999px"
   :background "transparent"
   :color "inherit"}
  [:.add-action
   {:height "auto" :align-self "stretch"
    :display "inline-flex" :align-items "center" :gap "6px"
    :padding "0 12px 0 10px"
    :border "none" :border-radius "999px"
    :background "transparent"
    :font-family "inherit"
    :font-size "13px" :font-weight "600" :line-height "1"
    :white-space "nowrap"
    :color "inherit"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]]
  ;; Splits the ＋ from the menu that changes what it adds, so the two read as
  ;; one control with two jobs rather than two buttons that happen to touch.
  [:.add-divider
   {:width "1px" :height "18px"
    :flex "0 0 auto"
    :background t/--note-kraft-18}]
  [:.add-menu
   {:width "26px" :height "26px"
    :flex "0 0 auto"
    :display "inline-flex" :align-items "center" :justify-content "center"
    :border "none" :border-radius "999px"
    :background "transparent"
    :padding "0"
    :color "inherit"
    :cursor "pointer"
    :transition "background 0.15s ease"}
   [:svg {:width "16px" :height "16px" :fill "currentColor"}]]
  ;; The UA focus ring is drawn in the browser's blue, which fights every header
  ;; tint; ours takes the card's own ink and shows for keyboard focus only.
  [#{:.add-action :.add-menu}
   {:outline "none"}
   [:&:focus-visible
    {:outline-width "2px" :outline-style "solid"
     :outline-color "currentColor" :outline-offset "1px"}]]
  ;; Hover feedback only where hover exists — on touch the browser pins :hover on
  ;; the last thing tapped, which the mobile cards already guard their own
  ;; buttons against.
  [:at-media {:hover "hover"}
   [#{:.add-action :.add-menu}
    [:&:hover {:background t/--note-kraft-10}]]])

(defn component
  "A card header's ＋ pill. `label` names what it adds — the noun alone, since
   the ＋ already says add — and `tooltip` says the rest on hover, in the house
   tooltip rather than the browser's own."
  [{:keys [label on-add aria-label tooltip]}]
  [pill
   (let [button [:button.add-action
                 (cond-> {:type "button" :on-click on-add}
                   aria-label (assoc :aria-label aria-label))
                 [icons/plus {:size 16}]
                 [:span label]]]
     (if tooltip
       [tt/tooltip {:content tooltip} button]
       button))])
